<template>
  <div
    class="stockItemCard"
    :class="{ removeable: showRemoveIcon }"
    @click="goToStockDetail"
  >
    <template v-if="stock">
      <IconComponent
        class="iconDv"
        icon="minus.svg"
        :id="`minusStock${stock.stockId}`"
        v-if="showRemoveIcon"
        @click.stop="onRemove()"
      />
      <StockImageTag
        :stockId="stock._id"
        :imgUrl="stock.logoUrl"
        :symbol="stock.symbol"
        :market="stock.type"
        :key="stock.logoUrl"
        :disableRouting="disableRouting"
      />
      <div class="textDv">
        <div class="headinginfoDv">
          <el-tooltip
            class="box-item"
            effect="dark"
            :content="stock.symbol"
            placement="top-start"
            v-if="isOverflowing"
          >
            <h1 ref="symbolElement">
              {{ stock.symbol }}
            </h1>
          </el-tooltip>
          <h1 v-else ref="symbolElement">
            {{ stock.symbol }}
          </h1>
        </div>
        <p v-if="fromSearchStock">
          {{ stock.name }}
        </p>
        <div class="priceRow">
          <p
            ref="priceElement"
            v-html="
              $filters.systemNumberConvention(stock.price, true, false, 110)
            "
          ></p>
          <span
            v-if="!fromSearchStock && stock.changesPercentage != null"
            :class="{ downward: stock.changesPercentage < 0 }"
          >
            <IconComponent class="priceIcon" icon="stock-pricing-up-icon.svg" />
            {{
              Math.abs(
                $filters.systemNumberConvention(
                  stock.changesPercentage,
                  false,
                  false
                )
              )
            }}%
          </span>
        </div>
      </div>
    </template>
  </div>
</template>

<script lang="ts">
import { useSocketStockData } from "@/composables/stock_data.composable";
import { SocketStock } from "@/helpers/models/stock/socket_stock.model";
import { RouteEnum } from "@/router/routeNames";
import router from "@/router/router";
import { popModal } from "jenesius-vue-modal";
import { defineComponent, nextTick, onMounted, ref, watch } from "vue";

export default defineComponent({
  props: {
    stockData: {
      type: [Object, null],
      required: false,
    },
    disableRouting: Boolean,
    showRemoveIcon: {
      type: Boolean,
      default: false,
    },
    fromSearchStock: Boolean,
  },
  setup(props, context) {
    const priceElement = ref<HTMLElement | null>(null);
    const widthCalculating = ref();

    const { stockData: stock, updateStockInfoObject } = useSocketStockData(
      props.stockData
    );

    const isOverflowing = ref(false);
    const symbolElement = ref<HTMLElement | null>(null);
    const checkOverflow = () => {
      nextTick(() => {
        if (symbolElement.value) {
          isOverflowing.value =
            symbolElement.value.scrollWidth > symbolElement.value.clientWidth;
        }
      });
    };

    onMounted(() => {
      checkOverflow();
    });

    watch(symbolElement, (newValue, oldValue) => {
      if (newValue) {
        checkOverflow();
      }
    });

    watch(
      () => props.stockData,
      (newStockData) => {
        if (newStockData) {
          updateStockInfoObject(newStockData);
          checkOverflow();
        }
      },
      { deep: true, immediate: true }
    );

    watch(
      () => props.stockData?.symbol,
      () => {
        checkOverflow();
      },
      { immediate: true }
    );

    onMounted(() => {
      if (priceElement.value) {
        const width = priceElement.value.offsetWidth;
        widthCalculating.value = width;
      }
      updateStockInfoObject(props.stockData);
    });

    watch(
      () => priceElement,
      () => {
        if (priceElement.value) {
          const width = priceElement.value.offsetWidth;
          widthCalculating.value = width;
        }
      }
    );

    const onRemove = () => context.emit("onRemove");

    const goToStockDetail = (ev: any) => {
      if (!props.disableRouting) {
        try {
          ev.stopPropagation();
        } catch (e) {}
        popModal();
        if (stock.value && stock.value?._id) {
          router.push({
            name: RouteEnum.stockDetail,
            params: { stockId: stock.value._id },
          });
        }
      }
    };
    watch(
      () => props.stockData,
      async (newStockData) => {
        updateStockInfoObject(props.stockData);
        await nextTick();
      },
      { deep: true }
    );

    return {
      goToStockDetail,
      onRemove,
      stock,
      priceElement,
      widthCalculating,
      isOverflowing,
      symbolElement,
      checkOverflow,
    };
  },
});
</script>
<style lang="scss" scoped>
.stock_logo.crypto {
  background: #fff;
}

.stockItemCard {
  width: var(--stock-item-widget-width);
  flex: 0 0 var(--stock-item-widget-width);
  max-width: var(--stock-item-widget-width);
  box-sizing: border-box;
  background: var(--base-light);
  border-radius: 10px;
  padding: 8px;
  border: 1px solid var(--base-new-border);
  gap: var(--tile-horizontal-gap);
  display: flex;
  position: relative;
  align-items: center;
  justify-content: flex-start;
  cursor: pointer;

  &.removeable {
    .textDv {
      .priceRow {
        span {
          padding-inline-end: 3px;
        }
      }
    }
  }

  .iconDv {
    position: absolute;
    top: 0px;
    cursor: pointer;
    right: -10px;
    width: 18px;

    height: auto;
    z-index: 99999;
  }

  .textDv {
    width: 100%;
    overflow: hidden;

    .headinginfoDv {
      display: flex;
      align-items: center;
      justify-content: flex-start;
      margin: 0px 0px 5px;
      gap: 2px;

      h1 {
        flex-grow: 1;
        min-width: 0;
        @include stockSymbol();
        margin: 0px;
        font-size: var(--font-2md);
        color: var(--color-label);
      }
    }

    .priceRow {
      display: flex;
      align-items: center;
      justify-content: flex-start;
      gap: 6px;
      flex-wrap: nowrap;

      p {
        @include stockPrice();
        margin: 0;
        margin-inline-end: 0px;
        color: var(--text-color);
        font-size: var(--font-2md);
        font-weight: 600;
        flex: 0 0 auto;
      }

      span {
        @include changePercent();
        height: auto;
        line-height: 1.3;
        flex: 0 0 auto;
        justify-content: flex-start;
        white-space: nowrap;
      }
    }
  }
}
[dir="rtl"] {
  .stockItemCard {
    .iconDv {
      right: auto;
      left: -10px;
    }
  }
}

@media screen and (min-width: 1280px) and (max-width: 1891px) {
  .stockItemCard {
    :deep(.stock_logo) {
      width: 30px !important;
      flex: 0 0 30px !important;
      height: 30px !important;
      border-width: 3px !important;
      margin-inline-end: 4px !important;
    }

    &.removeable {
      .textDv {
        .headinginfoDv {
          span {
          }
        }
      }
    }

    .iconDv {
    }

    .textDv {
      .headinginfoDv {
        h1 {
          font-size: var(--font-xs);
        }
      }

      .priceRow {
        p {
          font-size: var(--font-xs);
        }

        span {
          font-size: var(--font-2xs);
        }
      }
    }
  }
}

@media screen and (max-width: 767px) {
  .stockItemCard {
    width: var(--stock-item-widget-width);
    flex: 0 0 var(--stock-item-widget-width);
    max-width: var(--stock-item-widget-width);
  }
}

.highlight-price {
  background-color: yellow;
  transition: background-color 0.8s ease-in-out;
}
</style>
